import React from 'react';
import {ForecastMapService} from '../api';
import {ForecastMapPreviewCreateParams} from '../api/types';
import useApiClient from './useApiClient';

// Preview polling: 1.5 s → ×1.5 backoff → capped at 8 s, at most 40
// attempts before giving up. Previews render a single frame at capped dpi
// so they usually complete within a few polls.
export const PREVIEW_POLL_INITIAL_MS = 1500;
export const PREVIEW_POLL_BACKOFF = 1.5;
export const PREVIEW_POLL_MAX_MS = 8000;
export const PREVIEW_POLL_MAX_ATTEMPTS = 40;

export type PreviewState =
  | {status: 'idle'}
  // Keeps the previous image (if any) so the pane can dim it instead of
  // flashing empty on every re-render.
  | {status: 'rendering'; imageUrl: string | null}
  | {status: 'ready'; imageUrl: string; hour: number | null}
  | {status: 'failed'; errorDetail: string | null};

/**
 * Single-frame preview loop: POST previews/ with the current (possibly
 * unsaved) parameters, then poll GET previews/{id}/ with backoff until
 * completed/failed. A newer request invalidates any in-flight chain
 * (stale responses are ignored); unmount stops polling.
 */
const useForecastMapPreview = () => {
  const {fetch} = useApiClient();
  const [state, setState] = React.useState<PreviewState>({status: 'idle'});
  const seqRef = React.useRef(0);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Last known image, threaded into 'rendering' states.
  const lastImageRef = React.useRef<string | null>(null);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Invalidate any in-flight chain on unmount.
  React.useEffect(
    () => () => {
      seqRef.current += 1;
      clearTimer();
    },
    [clearTimer],
  );

  const request = React.useCallback(
    async (body: ForecastMapPreviewCreateParams) => {
      const seq = ++seqRef.current;
      clearTimer();
      setState({status: 'rendering', imageUrl: lastImageRef.current});

      // Recursive polling with backoff; function statement so the
      // self-reference is hoisted.
      function schedulePoll(previewId: number, attempt: number, delay: number) {
        timerRef.current = setTimeout(async () => {
          let settled = false;
          try {
            // silent: transient poll failures retry; terminal failure is
            // surfaced inline by the preview pane, not the global alert.
            const preview = await fetch(
              ForecastMapService.forecastMapPreviewDetail(previewId),
              {silent: true},
            );
            if (seqRef.current !== seq) {
              return; // a newer preview owns the pane
            }
            if (preview.status === 'completed' && preview.image_url) {
              lastImageRef.current = preview.image_url;
              setState({
                status: 'ready',
                imageUrl: preview.image_url,
                hour: preview.hour,
              });
              settled = true;
            } else if (preview.status === 'failed') {
              setState({status: 'failed', errorDetail: preview.error_detail});
              settled = true;
            }
          } catch (error) {
            // 404 is terminal: inline retention (newest-20/24h per owner)
            // reaped the row — e.g. a second tab previewing under the same
            // account — and it will never come back; polling the full
            // ~5-minute budget just shows "Rendering…" forever.
            const statusCode = (error as {statusCode?: number})?.statusCode;
            if (statusCode === 404) {
              if (seqRef.current === seq) {
                setState({status: 'failed', errorDetail: null});
              }
              return;
            }
            // other failures are transient — keep trying until the budget
            // runs out
          }
          if (settled || seqRef.current !== seq) {
            return;
          }
          if (attempt + 1 >= PREVIEW_POLL_MAX_ATTEMPTS) {
            setState({status: 'failed', errorDetail: null});
            return;
          }
          schedulePoll(
            previewId,
            attempt + 1,
            Math.min(delay * PREVIEW_POLL_BACKOFF, PREVIEW_POLL_MAX_MS),
          );
        }, delay);
      }

      try {
        // silent: a failed render request is surfaced inline (failed state
        // with error detail), never through the global alert.
        const created = await fetch(
          ForecastMapService.forecastMapPreviewCreate(body),
          {silent: true},
        );
        if (seqRef.current !== seq) {
          return;
        }
        schedulePoll(created.id, 0, PREVIEW_POLL_INITIAL_MS);
      } catch (error) {
        if (seqRef.current !== seq) {
          return;
        }
        const detail = (error as {details?: {message?: string}})?.details
          ?.message;
        setState({status: 'failed', errorDetail: detail ?? null});
      }
    },
    [fetch, clearTimer],
  );

  return {state, request};
};

export default useForecastMapPreview;
